import { useEffect, useState } from "react";
import { Button, Modal, Tooltip } from "flowbite-react";
import { HiOutlinePlus } from "react-icons/hi";
import QRScanner from "./QRScanner/QRScanner";
import { useAppSelector } from "../../Reducers/store";
import { Event } from "../../Models/Event";
import { toast } from "react-toastify";
import EventApi from "../../Api/EventApi";
import EventsListTable from "./EventsListTable";
import Loader from "../Common/Loader";
import AddEditEventModal from "./AddEditEventModal";
import CopyConferenceModal from "./CopyConferenceModal";
import EventParticipantsModal from "./Participants/EventParticipantsModal";
import { UserLevelNames } from "../../Models/UserLevel";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../../Utils/analytics";

export default function Events() {
  const [showQRScanner, setshowQRScanner] = useState<boolean>(false);
  const { userModel } = useAppSelector((state) => state.user);
  const [eventListData, seteventListData] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showEvent, setshowEvent] = useState<boolean>(false);
  const [showParticipants, setshowParticipants] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [conferenceToCopy, setConferenceToCopy] = useState<Event | null>(null);
  const [isCopying, setIsCopying] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getEvents();
  }, [userModel]);

  useEffect(() => {
    if (userModel && userModel.userLevelId !== UserLevelNames.SuperAdmin) {
      toast.error(
        "You do not have permission to access this page. Please contact an administrator if you believe this is an error."
      );
      // Redirect to home or unauthorized page
      navigate("/"); // or another route like "/unauthorized"
    }
  }, [userModel, navigate]);

  const getEvents = () => {
    try {
      setIsLoading(true);
      return EventApi.getEventList()
        .then((res) => {
          seteventListData(res.data);
          setIsLoading(false);
        })
        .catch((err) => {
          toast.error("Error");
          console.error(err);
          setIsLoading(false);
        });
    } catch (error) {
      toast.error("Error");
      console.error(error);
      setIsLoading(false);
    }
  };

  function onCloseModal() {
    setshowQRScanner(false);
  }

  const handleAddUser = (ev: Event) => {
    setSelectedEvent(ev);
    setshowQRScanner(true);
  };
  const handleAddUserSave = () => {
    getEvents();
    setSelectedEvent(null);
    onCloseModal();
  };
  const handleEditEvent = (ev: Event | null) => {
    setSelectedEvent(ev);
    setshowEvent(true);
  };
  const handleShowParticipants = (ev: Event | null) => {
    setSelectedEvent(ev);
    setshowParticipants(true);
  };
  const handleDeleteEvent = (ev: Event) => {
    const shouldRemove = window.confirm(
      "Are you sure you want to delete this Event?"
    );

    if (shouldRemove) {
      return EventApi.deleteEvent(ev.id)
        .then((res) => {
          if (res.data.success) {
            toast.success("Event Deleted");
            trackEvent("event_deleted", {
              event_id: ev.id,
              event_type: ev.eventType ?? "RegularEvent",
            });
          }
          getEvents();
        })
        .catch((err) => {
          toast.error("Error");
          console.error(err);
        });
    }
  };
  const handleCopyConferenceClick = (ev: Event) => {
    setConferenceToCopy(ev);
    setShowCopyModal(true);
  };

  const handleCopyConferenceConfirm = async () => {
    if (!conferenceToCopy) return;
    try {
      setIsCopying(true);
      const res = await EventApi.copyConference(conferenceToCopy.id);
      if (res.data.success) {
        toast.success(res.data.message ?? "Conference copied successfully");
        trackEvent("conference_copied", {
          source_event_id: conferenceToCopy.id,
          event_type: conferenceToCopy.eventType ?? "Conference",
        });
        setShowCopyModal(false);
        setConferenceToCopy(null);
        getEvents();
      } else {
        toast.error(res.data.message ?? "Failed to copy conference");
      }
    } catch (err) {
      toast.error("Failed to copy conference");
      console.error(err);
    } finally {
      setIsCopying(false);
    }
  };

  const handleCopyModalClose = () => {
    if (!isCopying) {
      setShowCopyModal(false);
      setConferenceToCopy(null);
    }
  };

  const getEventReport = async (ev: Event) => {
    try {
      setIsLoading(true);
      const response = await EventApi.getEventReport(ev.id);

      // Create a Blob from the response data
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });

      // Create a temporary URL for the Blob
      const url = window.URL.createObjectURL(pdfBlob);

      // Create a temporary <a> element to trigger the download
      const tempLink = document.createElement("a");
      tempLink.href = url;
      tempLink.setAttribute("download", `${ev.name}_Report.pdf`); // Set the desired filename for the downloaded file

      // Append the <a> element to the body and click it to trigger the download
      document.body.appendChild(tempLink);
      tempLink.click();

      // Clean up the temporary elements and URL
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(url);
      setIsLoading(false);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      setIsLoading(false);
    }
  };

  const handleDeleteParticipant = (participant: any) => {
    if (!selectedEvent) return;

    const participantName =
      participant.user?.firstName && participant.user?.lastName
        ? `${participant.user.firstName} ${participant.user.lastName}`
        : participant.memberName || "Unknown";

    const shouldRemove = window.confirm(
      `Are you sure you want to remove ${participantName} from this event?`
    );

    if (shouldRemove) {
      return EventApi.deleteEventParticipant(selectedEvent.id, participant.id)
        .then((res) => {
          if (res.data.success) {
            toast.success(res.data.message || "Participant removed. A notification email has been sent.");
            getEvents(); // Refresh the events list to update the participants
          } else {
            toast.error(res.data.message || "Failed to remove participant");
          }
        })
        .catch((err) => {
          toast.error("Error removing participant");
          console.error(err);
        });
    }
  };

  const qrScanner =
    showQRScanner && selectedEvent ? (
      <QRScanner
        selectedEvent={selectedEvent}
        handleAddUserSave={handleAddUserSave}
      />
    ) : null;
  return (
    <>
      <div className="flex justify-end px-4 pt-4">
        <Tooltip content="Add Event">
          <Button onClick={() => handleEditEvent(null)} className="buttonMain">
            <HiOutlinePlus />
          </Button>
        </Tooltip>
      </div>
      <div className="w-full">
        <div className="w-full p-4">
          {isLoading && <Loader />}
          <EventsListTable
            data={eventListData}
            handleEditEvent={handleEditEvent}
            handleAddUser={handleAddUser}
            deleteEvent={handleDeleteEvent}
            handleShowParticipants={handleShowParticipants}
            handleDownloadReport={getEventReport}
            handleCopyConference={handleCopyConferenceClick}
            isSuperUser={userModel?.userLevelId === UserLevelNames.SuperAdmin}
          />
        </div>
        <AddEditEventModal
          show={showEvent}
          setShow={setshowEvent}
          selectedEvent={selectedEvent}
          setselectedEvent={setSelectedEvent}
          getEvents={getEvents}
        />
      </div>
      <Modal show={showQRScanner} size="md" onClose={onCloseModal} popup>
        <Modal.Header></Modal.Header>
        <Modal.Body>
          <div>{qrScanner}</div>
        </Modal.Body>
      </Modal>
      <EventParticipantsModal
        show={showParticipants}
        setShow={setshowParticipants}
        selectedEvent={selectedEvent}
        onDeleteParticipant={handleDeleteParticipant}
      />
      <CopyConferenceModal
        show={showCopyModal}
        conference={conferenceToCopy}
        isCopying={isCopying}
        onClose={handleCopyModalClose}
        onConfirm={handleCopyConferenceConfirm}
      />
    </>
  );
}
